import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';

import {
  PlanCancellationResponse,
  GetPlansResponse,
  GetPlanDetailResponse,
  PlanChangeRequest,
  PlanHistoryResponse,
} from '../models/plan.model';

import { ApiService } from './api.service';

@Injectable({
  providedIn: 'root',
})
export class PlanService {
  private readonly API_URL = '/plan';
  private selectedPlanId: string | null = null;
  private apiService = inject(ApiService);


  planCancellation(planUuid: string): Observable<PlanCancellationResponse> {
    const url = `${this.API_URL}/${planUuid}/cancellation`;
    return this.apiService.post<PlanCancellationResponse>(url, {});
  }

  getPlansByUser(userUuid: string): Observable<GetPlansResponse> {
    const url = `${this.API_URL}/details/${userUuid}`;
    return this.apiService.get<GetPlansResponse>(url);
  }

  private buildPlansByUserParams(params: {
    sortField?: string;
    sortDirection?: string;
    page?: number;
    size?: number;
    status?: string;
    searchTerm?: string;
    contractId?: string;
  }): HttpParams {
    let httpParams = new HttpParams();

    if (params.sortField && params.sortDirection) {
      httpParams = httpParams.set(
        'sort',
        `${params.sortField},${params.sortDirection}`
      );
    }

    if (params.page !== undefined) {
      httpParams = httpParams.set('page', String(params.page));
    }
    if (params.size !== undefined) {
      httpParams = httpParams.set('size', String(params.size));
    }

    if (params.status) {
      httpParams = httpParams.set('status', params.status);
    }

    const trimmedSearch = (params.searchTerm ?? '').trim();
    if (trimmedSearch) {
      httpParams = httpParams.set('searchTerm', trimmedSearch);
    }

    if (params.contractId) {
      httpParams = httpParams.set('contractId', params.contractId);
    }

    return httpParams;
  }

  getPlansByUserWithSort(
    userUuid: string,
    params: {
      sortField?: string;
      sortDirection?: string;
      page?: number;
      size?: number;
      status?: string;
      searchTerm?: string;
      contractId?: string;
    } = {}
  ): Observable<GetPlansResponse> {
    const url = `${this.API_URL}/details/${userUuid}`;
    const httpParams = this.buildPlansByUserParams(params);
    return this.apiService.get<GetPlansResponse>(url, { params: httpParams });
  }

  getPlanDetailByUuid(planUuid: string): Observable<GetPlanDetailResponse> {
    const url = `${this.API_URL}/detailv2/${planUuid}`;
    return this.apiService.get<GetPlanDetailResponse>(url);
  }

  changePlan(payload: PlanChangeRequest): Observable<any> {
    const url = `${this.API_URL}/change`;
    return this.apiService.post<any>(url, payload);
  }

  getPlanHistory(planUuid: string): Observable<PlanHistoryResponse> {
    const url = `${this.API_URL}/history/${planUuid}`;
    return this.apiService.get<PlanHistoryResponse>(url);
  }

  setSelectedPlanId(uuid: string): void {
    this.selectedPlanId = uuid;
  }

  getSelectedPlanId(): string | null {
    return this.selectedPlanId;
  }

  clearSelectedPlanId(): void {
    this.selectedPlanId = null;
  }
}
